import Environment from '../utils/Environment';
import {SupportedCountryCode} from '../utils/currency';

// I21 — second-chance offer for the dismiss treatment.
//
// The offer is a 14-day free trial → annual plan (a longer-trial + lower-price
// downsell from the control's 7-day free trial). It reuses the already-provisioned
// `14d0_annual_60` / `14d0_annual_36` Stripe prices that power the TwoWeekOffer
// screen (`src/screens/TwoWeekOffer/TwoWeekOfferScreen.tsx`) — no new backend/Stripe
// plan is created here. If those price IDs ever move, update both places.

const inProduction = Environment.isProduction;

export type SecondChanceOffer = {
  /** Stripe price code passed straight to `stripe-dynamic-checkout` as `code`. */
  code: string;
  /** Localized annual price for display, e.g. "$59.99". */
  annualPrice: string;
  /** Annual price ÷ 12 for display, e.g. "$5.00". Hardcoded, not computed (see CLAUDE.md). */
  monthlyPrice: string;
  /** "Due today" amount for the free trial, in the locale's currency, e.g. "$0". */
  dueToday: string;
  /** Free-trial length in days (drives the post-purchase confirmation copy). */
  daysFree: number;
};

// 14-day free trial → annual (standard). Mirrors TwoWeekOffer `enhancedProducts`.
export const secondChanceOffers: Record<SupportedCountryCode, SecondChanceOffer> = {
  US: {
    code: inProduction ? 'price_1RMXY6H7jib4x1GyqeYCFEf0' : 'price_1RMXY6H7jib4x1Gy5Yr4Nz2e',
    annualPrice: '$59.99',
    monthlyPrice: '$5.00',
    dueToday: '$0',
    daysFree: 14,
  },
  GB: {
    code: inProduction ? 'price_1RMXY8H7jib4x1GyQpBtm841' : 'price_1RMXY7H7jib4x1GysbsHGCSI',
    annualPrice: '£49.99',
    monthlyPrice: '£4.17',
    dueToday: '£0',
    daysFree: 14,
  },
  DE: {
    code: inProduction ? 'price_1RMXY9H7jib4x1Gy3LoegeFe' : 'price_1RMXY9H7jib4x1Gy1FQxgj0g',
    annualPrice: '€59.99',
    monthlyPrice: '€5.00',
    dueToday: '€0',
    daysFree: 14,
  },
  AU: {
    code: inProduction ? 'price_1RMXYBH7jib4x1GyXtGzcp7W' : 'price_1RMXYAH7jib4x1GyhHNvaAeK',
    annualPrice: 'A$85.99',
    monthlyPrice: 'A$7.17',
    dueToday: 'A$0',
    daysFree: 14,
  },
  CA: {
    code: inProduction ? 'price_1RMXYCH7jib4x1Gy8cAyXcpE' : 'price_1RMXYCH7jib4x1GytJrTGIdF',
    annualPrice: 'C$79.99',
    monthlyPrice: 'C$6.67',
    dueToday: 'C$0',
    daysFree: 14,
  },
};

// 14-day free trial → annual (student). Mirrors TwoWeekOffer `studentOffers`.
export const studentSecondChanceOffers: Record<SupportedCountryCode, SecondChanceOffer> = {
  US: {
    code: inProduction ? 'price_1RMYSQH7jib4x1Gyk4kCyqGi' : 'price_1RMYSQH7jib4x1GyG4dyWQLN',
    annualPrice: '$35.99',
    monthlyPrice: '$3.00',
    dueToday: '$0',
    daysFree: 14,
  },
  GB: {
    code: inProduction ? 'price_1RMYSSH7jib4x1Gym3cWBnb2' : 'price_1RMYSRH7jib4x1GyVlPVLyhw',
    annualPrice: '£29.99',
    monthlyPrice: '£2.50',
    dueToday: '£0',
    daysFree: 14,
  },
  DE: {
    code: inProduction ? 'price_1RMYSUH7jib4x1GyQK9SSFdZ' : 'price_1RMYSTH7jib4x1GyblPnBAiN',
    annualPrice: '€34.99',
    monthlyPrice: '€2.92',
    dueToday: '€0',
    daysFree: 14,
  },
  AU: {
    code: inProduction ? 'price_1RMYSVH7jib4x1GykIDGUJN4' : 'price_1RMYSVH7jib4x1GyLqrQl2C4',
    annualPrice: 'A$50.99',
    monthlyPrice: 'A$4.25',
    dueToday: 'A$0',
    daysFree: 14,
  },
  CA: {
    code: inProduction ? 'price_1RMYSXH7jib4x1GyCUw4yWDT' : 'price_1RMYSWH7jib4x1Gy6AUKN9nB',
    annualPrice: 'C$47.99',
    monthlyPrice: 'C$4.00',
    dueToday: 'C$0',
    daysFree: 14,
  },
};

export const getSecondChanceOffer = (locale: SupportedCountryCode, isStudent: boolean): SecondChanceOffer =>
  (isStudent ? studentSecondChanceOffers : secondChanceOffers)[locale];
